export const PAGE_SIZES = [10, 25, 50, 100] as const;

type TablePreference = 'table-order' | 'page-size' | 'expand-all';

function readPreference(tableId: string, preference: TablePreference): string | null {
    try {
        return window.localStorage.getItem(`honeycomb:${preference}:${tableId}`);
    } catch {
        return null;
    }
}

function writePreference(tableId: string, preference: TablePreference, value: string | null): void {
    try {
        const key = `honeycomb:${preference}:${tableId}`;
        if (value === null) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, value);
        }
    } catch {
        // Persistence is optional; table preferences still work through the URL.
    }
}

export function parsePageSize(value: string | number | null): number | undefined {
    return PAGE_SIZES.find((size) => String(size) === String(value));
}

export function readPageSize(tableId: string): number | undefined {
    return parsePageSize(readPreference(tableId, 'page-size'));
}

export function writePageSize(tableId: string, pageSize: number): void {
    if (parsePageSize(pageSize) !== undefined) {
        writePreference(tableId, 'page-size', String(pageSize));
    }
}

export function isValidTableOrder(
    orderBy: string | null,
    sortableFields: readonly string[]
): boolean {
    if (!orderBy) {
        return false;
    }

    const field = orderBy.startsWith('-') ? orderBy.slice(1) : orderBy;
    return sortableFields.includes(field);
}

export function readTableOrder(tableId: string, sortableFields: readonly string[]): string | null {
    const orderBy = readPreference(tableId, 'table-order');
    return isValidTableOrder(orderBy, sortableFields) ? orderBy : null;
}

export function writeTableOrder(
    tableId: string,
    orderBy: string,
    sortableFields: readonly string[]
): void {
    if (!isValidTableOrder(orderBy, sortableFields)) {
        throw new Error(`Invalid orderBy value "${orderBy}" for table "${tableId}"`);
    }

    writePreference(tableId, 'table-order', orderBy);
}

export function removeTableOrder(tableId: string): void {
    writePreference(tableId, 'table-order', null);
}

export function readExpandAll(tableId: string): boolean {
    return readPreference(tableId, 'expand-all') === 'true';
}

export function writeExpandAll(tableId: string, expandAll: boolean): void {
    writePreference(tableId, 'expand-all', expandAll ? 'true' : null);
}
